/** Optional validation always identifies the unchanged locked bytes and calibration. */
import { z } from 'zod';
import { sha256HexSchema } from './canonicalJson';
import { hostTargetSchema } from './pipelineContracts';

export const lockedMacroValidationRequestSchema = z.object({ expectedRev: z.number().int().positive().safe(),
  sourceDigest: sha256HexSchema, packageSha256: sha256HexSchema, artifactDigest: sha256HexSchema,
  selectedHost: hostTargetSchema, mode: z.enum(['observe', 'execute']),
}).strict();
export type LockedMacroValidationRequest = z.infer<typeof lockedMacroValidationRequestSchema>;
export const lockedMacroValidationFailureSchema = z.object({ code: z.string().min(1).max(128),
  message: z.string().min(1).max(1024), nodeId: z.string().uuid().optional(), regionId: z.string().uuid().optional(),
}).strict();
export const lockedMacroValidationResultSchema = lockedMacroValidationRequestSchema.extend({
  status: z.enum(['passed', 'failed', 'cancelled']), failures: z.array(lockedMacroValidationFailureSchema).max(16_384),
}).strict().refine(result => result.status === 'passed' ? result.failures.length === 0
  : result.status !== 'failed' || result.failures.length > 0, 'Passed validation has no failures; failed validation requires a reason');
export type LockedMacroValidationResult = z.infer<typeof lockedMacroValidationResultSchema>;

/** One explicit invocation; transport retries must reuse this identity instead of executing again. */
export const lockedMacroValidationOperationSchema = z.object({ operationId: z.string().uuid(),
  validation: lockedMacroValidationRequestSchema }).strict();
export const lockedMacroValidationOperationStateSchema = z.discriminatedUnion('phase', [
  lockedMacroValidationOperationSchema.extend({ phase: z.literal('running') }).strict(),
  lockedMacroValidationOperationSchema.extend({ phase: z.literal('settled'), result: lockedMacroValidationResultSchema }).strict(),
]).refine(state => state.phase === 'running' || validateLockedMacroValidationExchange(state.validation, state.result),
  'The operation result must identify its exact validation request');
export type LockedMacroValidationOperation = z.infer<typeof lockedMacroValidationOperationSchema>;
export type LockedMacroValidationOperationState = z.infer<typeof lockedMacroValidationOperationStateSchema>;

export function validateLockedMacroValidationExchange(request: LockedMacroValidationRequest,
  result: LockedMacroValidationResult): boolean {
  return lockedMacroValidationRequestSchema.safeParse(request).success
    && lockedMacroValidationResultSchema.safeParse(result).success
    && ['expectedRev', 'sourceDigest', 'packageSha256', 'artifactDigest', 'mode'].every(key =>
      request[key as keyof LockedMacroValidationRequest] === result[key as keyof LockedMacroValidationRequest])
    && ['hostId', 'buildId', 'capabilitiesDigest'].every(key =>
      request.selectedHost[key as keyof typeof request.selectedHost] === result.selectedHost[key as keyof typeof result.selectedHost]);
}
